/**
 * TabSystem - Custom web component that handles tab functionality
 * Usage:
 * <x-tabbed-content>
 *   <div class="tab-list" role="tablist">
 *     <button role="tab" aria-controls="panel-1" aria-selected="true">Tab 1</button>
 *     <button role="tab" aria-controls="panel-2" aria-selected="false">Tab 2</button>
 *   </div>
 *   <div id="panel-1" role="tabpanel">Panel 1 content</div>
 *   <div id="panel-2" role="tabpanel" hidden>Panel 2 content</div>
 * </x-tabbed-content>
 */
export class TabbedContent extends HTMLElement {
  private tabList: HTMLElement | null = null
  private tabs: HTMLElement[] = []
  private tabFocus: number = 0

  constructor() {
    super()

    // Bind methods
    this.changeTabs = this.changeTabs.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback(): void {
    // Find tablist and tabs in the component's content
    this.tabList = this.querySelector('[role="tablist"]')

    if (!this.tabList) {
      console.error('TabSystem requires a child element with role="tablist"')
      return
    }

    this.tabs = Array.from(this.tabList.querySelectorAll('[role="tab"]')) as HTMLElement[]

    if (this.tabs.length === 0) {
      console.error('TabSystem requires at least one tab element with role="tab"')
      return
    }

    // Set up initial state
    this.tabFocus = this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true')
    if (this.tabFocus === -1) this.tabFocus = 0

    // Set initial tab index
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === this.tabFocus ? '0' : '-1')
      tab.addEventListener('click', this.changeTabs)
    })

    // Add keyboard navigation
    this.tabList.addEventListener('keydown', this.handleKeydown)
  }

  disconnectedCallback(): void {
    // Clean up event listeners when component is removed
    this.tabs.forEach(tab => {
      tab.removeEventListener('click', this.changeTabs)
    })

    if (this.tabList) {
      this.tabList.removeEventListener('keydown', this.handleKeydown)
    }
  }

  changeTabs(e: Event): void {
    const targetTab = e.target as HTMLElement

    // Ignore clicks on elements that aren't tabs
    if (targetTab.getAttribute('role') !== 'tab') return

    // Remove all current selected tabs
    this.tabs.forEach(tab => {
      tab.setAttribute('aria-selected', 'false')
      tab.setAttribute('tabindex', '-1')
    })

    // Set this tab as selected
    targetTab.setAttribute('aria-selected', 'true')
    targetTab.setAttribute('tabindex', '0')

    // Update tab focus index
    this.tabFocus = this.tabs.indexOf(targetTab)

    // Hide all tab panels
    const tabPanels = Array.from(this.querySelectorAll('[role="tabpanel"]'))
    tabPanels.forEach(panel => {
      panel.setAttribute('hidden', 'true')
    })

    // Show the selected panel
    const panelId = targetTab.getAttribute('aria-controls')
    if (panelId) {
      const activePanel = this.querySelector(`#${panelId}`)
      if (activePanel) {
        activePanel.removeAttribute('hidden')
      }
    }
  }

  handleKeydown(e: KeyboardEvent): void {
    // Only handle arrow keys
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return

    // Prevent default behavior (scrolling)
    e.preventDefault()

    // Calculate the new tab index
    let newTabFocus = this.tabFocus

    if (e.key === 'ArrowRight') {
      newTabFocus++
      // If we're at the end, go to the start
      if (newTabFocus >= this.tabs.length) {
        newTabFocus = 0
      }
    } else if (e.key === 'ArrowLeft') {
      newTabFocus--
      // If we're at the start, move to the end
      if (newTabFocus < 0) {
        newTabFocus = this.tabs.length - 1
      }
    }

    // Update tabindex for all tabs
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === newTabFocus ? '0' : '-1')
    })

    // Focus the new tab
    this.tabs[newTabFocus].focus()
    this.tabFocus = newTabFocus
  }
}
